/**
 * SharePopover — 강의 머리의 공유 범위 판(설계 FE-20260912 §W05).
 *
 * 여는 것만으로는 쓰지 않는다 — 라디오로 고른 값은 판 안의 임시 값이고 [공유 변경]을 눌러야 저장한다.
 * 옛 강의 정보 카드는 공유 카드를 누르는 순간 곧바로 썼다(Syllabus.js SyllabusInfoPanel). 작은 판에서 훑어보다
 * 잘못 눌러도 공유가 바뀌지 않게 한 단계를 둔다. 저장이 끝나면 판을 닫는다(초점은 FloatingPanel 이 여는 버튼으로 돌려준다).
 *
 * 공유받은 강의는 범위와 주인만 보인다 — 원본의 공유는 주인만 바꾼다(회신 §4 P7). 설계 §W05 가 공유받은 사람에게도
 * 범위·주인을 보이라고 한다(옛 머리 배지는 「공유받음」 한 마디뿐이었다).
 */
import {
  useEffect, useId, useRef, useState,
} from 'react';
import { X } from 'lucide-react';
import { Button } from '../common';
import FloatingPanel from './FloatingPanel';
import { describeSyllabusError } from './syllabusApi';

/** 공유는 값 하나(회신 §4 P7) — 오너가 정한 상태가 정확히 셋이다. 문구는 옛 강의 정보 카드(Syllabus.js)의 것을 그대로 옮겼다 */
export const SHARE_OPTIONS = [
  { value: null, label: '개인', hint: '나만 봅니다 — 매니저도 볼 수 없습니다' },
  { value: 'managers', label: '매니저 공유', hint: '매니저·관리자 전원이 봅니다' },
  { value: 'all', label: '전체 공유', hint: '매니저·관리자·강사 전원이 봅니다' },
];
export const shareLabel = (s) => (SHARE_OPTIONS.find((o) => o.value === (s ?? null)) || SHARE_OPTIONS[0]).label;

export const panelErrorCls = 'rounded-2xl bg-red-50 p-3 text-xs text-red-600';
const closeCls = 'absolute right-1.5 top-1.5 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

function SharePopover({
  anchorRef, data, owner = false, busy = false, onShare, onClose, sheet = false,
}) {
  const current = data?.share_scope ?? null;
  const [pick, setPick] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const savingRef = useRef(false); // 같은 틱의 두 번 누름도 한 번만 보낸다(상태는 다음 렌더에야 잠긴다)
  const aliveRef = useRef(true);
  const name = useId();
  // 효과 안에서 다시 켠다 — StrictMode(개발 서버)는 효과를 걸었다 풀었다 다시 걸어서, 끄기만 하면 꺼진 채 남아 저장 뒤에도 판이 안 닫힌다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const changed = pick !== current;

  const submit = async () => {
    if (!changed || busy || savingRef.current || !onShare) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await onShare(pick);
      if (aliveRef.current) onClose('saved');
    } catch (e) {
      // 실패하면 판을 두고 사유를 적는다 — 고른 값도 그대로(다시 누르면 같은 값으로 다시 보낸다)
      if (aliveRef.current) setError(describeSyllabusError(e));
    } finally {
      savingRef.current = false;
      if (aliveRef.current) setSaving(false);
    }
  };

  return (
    <FloatingPanel anchorRef={anchorRef} onClose={onClose} align="end" sheet={sheet} label="공유 범위" className={sheet ? '' : 'w-72'}>
      <div className="px-2 pb-1 pt-0.5 text-xs font-bold text-gray-800">공유 범위</div>
      {owner ? (
        <>
          <fieldset className="space-y-0.5">
            <legend className="sr-only">공유 범위 고르기</legend>
            {SHARE_OPTIONS.map((o) => {
              const on = pick === o.value;
              return (
                <label
                  key={o.label}
                  className={`flex cursor-pointer items-start gap-2 rounded-xl px-2 py-1.5 transition-colors ${on ? 'bg-primary/10' : 'hover:bg-gray-50'}`}
                >
                  <input
                    type="radio"
                    name={name}
                    checked={on}
                    onChange={() => { setPick(o.value); setError(null); }}
                    disabled={saving}
                    className="mt-0.5 accent-primary"
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`flex items-center gap-1 text-xs ${on ? 'font-bold text-primary' : 'font-medium text-gray-700'}`}>
                      {o.label}
                      {/* 지금 저장된 범위 — 고른 것(초록)과 저장된 것이 다를 때 색 말고 글자로도 가른다 */}
                      {o.value === current && (
                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">지금</span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-gray-400">{o.hint}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>
          <p className="px-2 py-1.5 text-[11px] text-gray-500">공유받은 사람은 이 강의를 고칠 수 없습니다.</p>
          {error && <p role="alert" className={`mx-1 mb-1.5 ${panelErrorCls}`}>{error}</p>}
          <div className="flex items-center justify-end gap-1.5 border-t border-gray-100 px-1 pt-1.5">
            <Button size="xs" onClick={submit} disabled={!changed || busy} loading={saving}>공유 변경</Button>
          </div>
        </>
      ) : (
        <>
          <dl className="space-y-1 px-2 py-1 text-xs">
            <div className="flex gap-2">
              <dt className="w-8 shrink-0 text-gray-400">범위</dt>
              <dd className="min-w-0 font-medium text-gray-700">{shareLabel(current)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-8 shrink-0 text-gray-400">주인</dt>
              <dd className="min-w-0 truncate font-medium text-gray-700">{data?.author_name || '알 수 없음'}</dd>
            </div>
          </dl>
          <p className="px-2 pb-1 pt-0.5 text-[11px] text-gray-500">공유받은 사람은 이 강의를 고칠 수 없습니다.</p>
        </>
      )}
      {/* 닫기는 DOM 끝에 둔다 — 판을 열면 FloatingPanel 이 첫 입력에 초점을 주는데, 그 자리가 닫기가 아니라 라디오여야 해서 */}
      <button type="button" onClick={() => onClose('button')} aria-label="공유 범위 닫기" className={closeCls}>
        <X className="size-3.5" />
      </button>
    </FloatingPanel>
  );
}

export default SharePopover;
